/**
 * Attribution capture + retrieval for the /start landing page.
 *
 * Reads UTM params, fbclid, gclid, FB cookies (_fbp, _fbc), referrer, and
 * landing URL when the LP mounts. Stores them in sessionStorage under
 * `aven_attribution` — first-touch wins, subsequent loads preserve the
 * original ad click.
 *
 * Shape matches the backend's Attribution pydantic schema in
 * `backend/app/api/api_v1/endpoints/web_chat.py`.
 */

const STORAGE_KEY = 'aven_attribution';

function readCookie(name) {
  try {
    const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  } catch (_) {
    return null;
  }
}

function paramsFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get('utm_source') || null,
      utm_medium: p.get('utm_medium') || null,
      utm_campaign: p.get('utm_campaign') || null,
      utm_content: p.get('utm_content') || null,
      utm_term: p.get('utm_term') || null,
      fbclid: p.get('fbclid') || null,
      gclid: p.get('gclid') || null,
    };
  } catch (_) {
    return {};
  }
}

/**
 * Backfill `fbp` / `fbc` from cookies when the stored record is missing them.
 *
 * These two come from fbevents.js, which loads asynchronously — so on the
 * very first capture the cookies frequently do not exist yet, and we would
 * otherwise send `fbp: null` to CAPI forever and depress Event Match Quality.
 * Unlike the utm_ params and fbclid, this is NOT a first-touch violation: we
 * only ever fill a null, never overwrite a value the original ad click gave us.
 */
function backfillFbCookies(attribution) {
  let changed = false;
  if (!attribution.fbp) {
    const fbp = readCookie('_fbp');
    if (fbp) {
      attribution.fbp = fbp;
      changed = true;
    }
  }
  if (!attribution.fbc) {
    const fbc = readCookie('_fbc');
    if (fbc) {
      attribution.fbc = fbc;
      changed = true;
    }
  }
  if (changed) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
    } catch (_) {}
  }
  return attribution;
}

/**
 * Capture attribution. First-touch: if sessionStorage already has a stored
 * value, we DO NOT overwrite — the original ad click wins.
 *
 * Safe and cheap to call from anywhere, and every consumer should call THIS
 * rather than getAttribution(). React runs child effects before parent
 * effects, so when only the LP captured on mount, ChatWidget (a child) read
 * attribution before it had been written and created every web-chat session
 * with an empty payload — which is why funnel events carried a null fbclid.
 *
 * Returns the effective attribution object.
 */
export function captureAttribution() {
  // First-touch preservation
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return backfillFbCookies(JSON.parse(stored));
    }
  } catch (_) {}

  const urlParams = paramsFromUrl();
  const fbp = readCookie('_fbp');
  const fbc = readCookie('_fbc');

  const attribution = {
    ...urlParams,
    fbp: fbp || null,
    // _fbc is the canonical source; fall back to building one from fbclid
    fbc: fbc || (urlParams.fbclid ? `fb.1.${Date.now()}.${urlParams.fbclid}` : null),
    landing_url: (() => {
      try { return window.location.href; } catch (_) { return null; }
    })(),
    referrer: (() => {
      try { return document.referrer || null; } catch (_) { return null; }
    })(),
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch (_) {}

  return attribution;
}

export function clearAttribution() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
}
